import { DateTime } from "luxon";

import {
  filterDirectInviteCarrierACLs,
  generateClientId,
  getBookNowPriceDisplay,
  getSeededAvailableLoads,
  isBookNowEligibleShipmentType,
} from "utils";

import type { CreateShipmentFormState } from "types/shipment";

function updatePointForCopy(routePoint: RoutePoint): EditableRoutePointState {
  const updatedLocation: RoutePointLocation = {
    ...routePoint.point_location,
  };

  return {
    ...routePoint,
    clientId: generateClientId(),
    point_location: updatedLocation,
    start_time: DateTime.now().toFormat("yyyy-MM-dd"),
    isBeingEdited: false,
    inputMode: "autocomplete",
  };
}

function getCurrencyString(currencyId: number): string {
  if (currencyId === 1) {
    return "CAD";
  }
  return "USD";
}

export function createShipmentFormState(
  shipment: ShipperCondensedShipment,
  routePoints: RoutePoint[],
): CreateShipmentFormState {
  /*
  Creates a CreateShipmentFormState from a CondensedShipment in order to use as initial state
  in the shipment creation form.
  */
  const readableRoutePoints: EditableRoutePointState[] = [];
  routePoints.forEach((point) => {
    readableRoutePoints.push(updatePointForCopy(point));
  });

  const shipmentState: CreateShipmentFormState = {
    shipmentType:
      shipment.info.type === "MULTI_SHIPMENT_PROJECT_CHILD"
        ? "SINGLE"
        : shipment.info.type,
    equipmentTypes: shipment.equipments.map((equipment) => equipment.type),
    service: shipment.info.service,
    commodity: shipment.info?.commodity ? shipment.info?.commodity.code : "",
    weightLb: shipment.info.weight_lb,
    footage: shipment.info.footage ? shipment.info.footage.toString() : "",
    specialRequirements: shipment.info.special_requirements,
    additionalDetails: shipment.info.additional_details ?? "",
    preferredPrice: shipment.info.preferred_price
      ? shipment.info.preferred_price.toString()
      : "",
    routePoints: readableRoutePoints,
    currency: getCurrencyString(shipment.info.currency),
    notificationSettingsType:
      shipment?.network_notification_type ?? "ALL_CARRIER_NETWORK",
    notificationSetingsEmails: shipment?.carrier_acl
      ? filterDirectInviteCarrierACLs(shipment.carrier_acl).map(
          (acl) => acl.role.user.email,
        )
      : [],
    commodityFreetext: shipment?.info?.commodity_freetext ?? "",
    isBookNow:
      (shipment?.book_now_info?.is_book_now &&
        isBookNowEligibleShipmentType(shipment.info.type)) ??
      false,
    bookNowCurrency: shipment?.book_now_info?.book_now_currency ?? "CAD",
    bookNowPrice: getBookNowPriceDisplay(shipment),
    customName: shipment?.custom_name ?? "",
    emailNotificationType: "ALL",
    shareeRoleIds: shipment?.sharees
      ? shipment.sharees.map((sharee: ShipmentSharee) => sharee.sharee.id)
      : [],
    shareShipment: shipment?.sharees && shipment.sharees.length > 0,
    availableLoads: getSeededAvailableLoads(shipment.project?.loads_available),
    partnerGroups: shipment?.network_groups
      ? shipment.network_groups.map((group) => group.id.toString())
      : [],
    customerRoleIds:
      shipment?.supplier_shipment_info?.customer_roles?.map(
        (role: SupplierShipmentCustomerRole) => role.id,
      ) ?? [],
    externalCustomerIds:
      shipment?.supplier_shipment_info?.external_customers?.map(
        (customer: ExternalCustomerInfo) => customer.id,
      ) ?? [],
    customerInviteEmails: [], // Always empty for copied shipments to avoid duplicates
    customerSnapshots: [], // A copy re-resolves against the live network, not a saved snapshot
    files: [],
    quotingUnitOfMeasure: shipment.info.quoting_unit_of_measure,
    analytics: {
      shipmentCreationMode: "COPY",
      creationStartingTime: DateTime.now(),
      encounteredErrors: [],
      routePointAddressModes: {},
    },
    // Network invite emails for copied shipments
    // intentionally empty to avoid copying invite emails
    networkInviteEmails: [],
  };
  return shipmentState;
}
